import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ConfirmModal from '../../components/modals/ConfirmModal';
import {
  Building2,
  ArrowLeft,
  Save,
  Users,
  CreditCard,
  MapPin,
  Shield,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Plus,
  Minus,
  Settings,
  Loader2,
  Package,
  ToggleLeft,
  ToggleRight,
  Eye,
  ChevronDown,
  ChevronRight,
  Home,
  ShoppingCart,
  ShoppingBag,
  IndianRupee,
  TrendingUp,
  BarChart2
} from 'lucide-react';

// Icon mapping for modules
const MODULE_ICONS = {
  home: Home,
  catalogue: Package,
  sales: ShoppingCart,
  purchases: ShoppingBag,
  finance_billing: IndianRupee,
  team_operations: Users,
  field_tracking: MapPin,
  intelligence: TrendingUp,
  settings: Settings,
  reports: BarChart2
};

const MODULE_COLORS = {
  home: { bg: 'bg-slate-50', text: 'text-slate-700' },
  catalogue: { bg: 'bg-blue-50', text: 'text-blue-700' },
  sales: { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  purchases: { bg: 'bg-amber-50', text: 'text-amber-700' },
  finance_billing: { bg: 'bg-rose-50', text: 'text-rose-700' },
  team_operations: { bg: 'bg-indigo-50', text: 'text-indigo-700' },
  field_tracking: { bg: 'bg-teal-50', text: 'text-teal-700' },
  intelligence: { bg: 'bg-pink-50', text: 'text-pink-700' },
  settings: { bg: 'bg-purple-50', text: 'text-purple-700' }
};

/**
 * BusinessDetails - FULL Business Control Page
 * Admin has COMPLETE control over what each business can see
 */
const BusinessDetails = () => {
  const { id: businessId } = useParams();
  const navigate = useNavigate();

  // Data states
  const [business, setBusiness] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [menuConfig, setMenuConfig] = useState(null);
  const [moduleOverrides, setModuleOverrides] = useState({});
  const [submoduleOverrides, setSubmoduleOverrides] = useState({});
  const [limitOverrides, setLimitOverrides] = useState({});
  const [limitsSummary, setLimitsSummary] = useState(null);
  
  // Form states
  const [selectedIndustryId, setSelectedIndustryId] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [addons, setAddons] = useState({
    extra_staff_seats: 0,
    extra_tracking_seats: 0
  });
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('plan'); // 'plan' | 'menu' | 'usage'
  const [expandedModules, setExpandedModules] = useState({});
  const [confirmModal, setConfirmModal] = useState(null); // { title, description, confirmLabel, confirmClass, onConfirm }

  // Fetch business details
  const fetchBusiness = useCallback(async () => {
    try {
      const res = await api.get(`/admin/businesses/${businessId}`);
      setBusiness(res.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load business details'));
    }
  }, [businessId]);

  // Fetch industries
  const fetchIndustries = useCallback(async () => {
    try {
      const res = await api.get('/admin/plans-v2/industries');
      setIndustries(res.data.industries || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load industries'));
    }
  }, []);

  // Fetch plans for selected industry
  const fetchPlans = useCallback(async (industryId) => {
    if (!industryId) {
      setPlans([]);
      return;
    }
    try {
      const res = await api.get(`/admin/plans-v2/plans?industry_id=${industryId}`);
      setPlans(res.data.plans || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load plans'));
    }
  }, []);

  // Fetch current subscription and overrides
  const fetchSubscription = useCallback(async () => {
    try {
      const res = await api.get(`/admin/businesses/${businessId}/subscription`);
      const data = res.data;
      setSubscription(data);
      if (data.industry_id) {
        setSelectedIndustryId(data.industry_id);
        fetchPlans(data.industry_id);
      }
      if (data.plan_id) {
        setSelectedPlanId(data.plan_id);
      }
      if (data.add_ons) {
        setAddons({
          extra_staff_seats: data.add_ons.extra_staff_seats || 0,
          extra_tracking_seats: data.add_ons.extra_tracking_seats || 0
        });
      }
      const overrides = data.overrides || {};
      setModuleOverrides(overrides.modules || {});
      setSubmoduleOverrides(overrides.submodules || {});
      setLimitOverrides(overrides.limits || {});
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load subscription'));
    }
  }, [businessId, fetchPlans]);

  // Fetch menu config for the industry
  const fetchMenuConfig = useCallback(async () => {
    if (!selectedIndustryId) return;
    try {
      const res = await api.get(`/admin/plans-v2/industries/${selectedIndustryId}/menu-config`);
      const data = res.data;
      setMenuConfig(data.menu_config);
      const expanded = {};
      (data.menu_config?.modules || []).forEach(m => {
        expanded[m.key] = true;
      });
      setExpandedModules(expanded);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load menu config'));
    }
  }, [selectedIndustryId]);

  // Fetch limits summary
  const fetchLimitsSummary = useCallback(async () => {
    try {
      const res = await api.get(`/admin/businesses/${businessId}/limits-summary`);
      setLimitsSummary(res.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load usage data'));
    }
  }, [businessId]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchBusiness(),
        fetchIndustries(),
        fetchSubscription(),
        fetchLimitsSummary()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchBusiness, fetchIndustries, fetchSubscription, fetchLimitsSummary]);

  // Load menu config when industry changes
  useEffect(() => {
    if (selectedIndustryId) {
      fetchPlans(selectedIndustryId);
      fetchMenuConfig();
    }
  }, [selectedIndustryId, fetchPlans, fetchMenuConfig]);

  // Toggle module expansion
  const toggleModuleExpand = (moduleKey) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleKey]: !prev[moduleKey]
    }));
  };

  // Toggle module override
  const toggleModuleOverride = (moduleKey) => {
    setModuleOverrides(prev => {
      const current = prev[moduleKey];
      // Cycle: undefined -> true -> false -> undefined
      if (current === undefined) return { ...prev, [moduleKey]: true };
      if (current === true) return { ...prev, [moduleKey]: false };
      const { [moduleKey]: _, ...rest } = prev;
      return rest;
    });
  };

  // Toggle submodule override
  const toggleSubmoduleOverride = (submoduleKey) => {
    setSubmoduleOverrides(prev => {
      const current = prev[submoduleKey];
      if (current === undefined) return { ...prev, [submoduleKey]: true };
      if (current === true) return { ...prev, [submoduleKey]: false };
      const { [submoduleKey]: _, ...rest } = prev;
      return rest;
    });
  };

  // Save all changes
  const handleSave = async () => {
    if (!selectedIndustryId || !selectedPlanId) {
      toast.error('Please select both industry and plan');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/admin/businesses/${businessId}/subscription`, {
        industry_id: selectedIndustryId,
        plan_id: selectedPlanId,
        add_ons: addons,
        overrides: {
          modules: moduleOverrides,
          submodules: submoduleOverrides,
          limits: limitOverrides
        }
      });
      toast.success('Business settings saved successfully');
      await fetchSubscription();
      await fetchLimitsSummary();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save changes'));
    } finally {
      setSaving(false);
    }
  };

  // Get override status display
  const getOverrideStatus = (key, overrides) => {
    const value = overrides[key];
    if (value === undefined) return { text: 'Use Plan', color: 'text-slate-400', bg: 'bg-slate-100' };
    if (value === true) return { text: 'Force ON', color: 'text-emerald-600', bg: 'bg-emerald-100' };
    return { text: 'Force OFF', color: 'text-red-600', bg: 'bg-red-100' };
  };

  // Get toggle color
  const getToggleColor = (key, overrides) => {
    const value = overrides[key];
    if (value === true) return 'text-emerald-500';
    if (value === false) return 'text-red-400';
    return 'text-slate-300';
  };

  if (loading) {
    return (
      <div className="h-full flex flex-col bg-white" data-testid="business-details-page">
        <div className="flex-1 flex items-center justify-center bg-[#F8F9FB]">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-2 border-rose-200 border-t-rose-500 rounded-full animate-spin" />
            <p className="text-slate-500 text-sm font-light">Loading business details...</p>
          </div>
        </div>
      </div>
    );
  }

  const selectedPlan = plans.find(p => p.id === selectedPlanId);

  return (
    <div className="h-full flex flex-col bg-white" data-testid="business-details-page">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/businesses')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <div>
                <h1 className="text-[18px] font-semibold text-slate-800">Business Control</h1>
                <p className="text-[12px] text-slate-500 mt-0.5">
                  {business?.name || 'Loading...'} • Full access control
                </p>
              </div>
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 bg-rose-600 text-white rounded-lg text-[13px] font-medium hover:bg-rose-700 transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save All Changes
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 border-t border-slate-100">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('plan')}
              className={`px-4 py-3 text-[13px] font-medium border-b-2 transition-colors ${
                activeTab === 'plan'
                  ? 'text-rose-600 border-rose-600'
                  : 'text-slate-500 border-transparent hover:text-slate-700'
              }`}
            >
              Plan & Limits
            </button>
            <button
              onClick={() => setActiveTab('menu')}
              className={`px-4 py-3 text-[13px] font-medium border-b-2 transition-colors ${
                activeTab === 'menu'
                  ? 'text-rose-600 border-rose-600'
                  : 'text-slate-500 border-transparent hover:text-slate-700'
              }`}
            >
              Menu Control
            </button>
            <button
              onClick={() => setActiveTab('usage')}
              className={`px-4 py-3 text-[13px] font-medium border-b-2 transition-colors ${
                activeTab === 'usage'
                  ? 'text-rose-600 border-rose-600'
                  : 'text-slate-500 border-transparent hover:text-slate-700'
              }`}
            >
              Usage & Counters
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto bg-[#F8F9FB] p-6">
        <div className="max-w-5xl mx-auto">
          
          {/* Plan & Limits Tab */}
          {activeTab === 'plan' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column */}
              <div className="lg:col-span-2 space-y-6">
                {/* Business Info */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-rose-600" />
                    </div>
                    <div>
                      <h3 className="text-[16px] font-semibold text-slate-800">{business?.name}</h3>
                      <p className="text-[12px] text-slate-500">{business?.email} • Owner: {business?.owner?.name || business?.owner?.email || 'N/A'}</p>
                    </div>
                    <span className={`ml-auto px-3 py-1 rounded-full text-[11px] font-medium ${
                      business?.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {business?.status === 'inactive' || business?.status === 'suspended' ? 'Suspended' : (business?.status || 'Active')}
                    </span>
                  </div>
                </div>

                {/* Plan Selection */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                  <h3 className="text-[14px] font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-rose-500" />
                    Plan Selection
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[12px] font-medium text-slate-600 mb-1.5">Industry *</label>
                      <select
                        value={selectedIndustryId}
                        onChange={(e) => {
                          setSelectedIndustryId(e.target.value);
                          setSelectedPlanId('');
                        }}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-rose-200"
                      >
                        <option value="">Select Industry</option>
                        {industries.map(ind => (
                          <option key={ind.id} value={ind.id}>{ind.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[12px] font-medium text-slate-600 mb-1.5">Plan *</label>
                      <select
                        value={selectedPlanId}
                        onChange={(e) => setSelectedPlanId(e.target.value)}
                        disabled={!selectedIndustryId}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-rose-200 disabled:bg-slate-50"
                      >
                        <option value="">Select Plan</option>
                        {plans.map(plan => (
                          <option key={plan.id} value={plan.id}>{plan.name} - ₹{plan.price_monthly}/mo</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {selectedPlan && (
                    <div className="mt-4 p-4 bg-rose-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-[14px] font-semibold text-slate-800">{selectedPlan.name}</p>
                          <p className="text-[11px] text-slate-500">Base Staff: {selectedPlan.included_staff} • Base Tracking: {selectedPlan.included_tracking_staff}</p>
                        </div>
                        <p className="text-[20px] font-bold text-rose-600">₹{selectedPlan.price_monthly}<span className="text-[11px] font-normal text-slate-500">/mo</span></p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Add-ons */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                  <h3 className="text-[14px] font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <Plus className="w-4 h-4 text-rose-500" />
                    Add-ons
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-slate-600" />
                          <span className="text-[13px] font-medium text-slate-700">Extra Staff Seats</span>
                        </div>
                        <span className="text-[11px] text-slate-500">₹99/seat/mo</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setAddons(prev => ({ ...prev, extra_staff_seats: Math.max(0, prev.extra_staff_seats - 1) }))}
                          className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-white"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="text-[18px] font-bold text-slate-800 w-8 text-center">{addons.extra_staff_seats}</span>
                        <button
                          onClick={() => setAddons(prev => ({ ...prev, extra_staff_seats: prev.extra_staff_seats + 1 }))}
                          className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-white"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-600" />
                          <span className="text-[13px] font-medium text-slate-700">Extra Tracking Seats</span>
                        </div>
                        <span className="text-[11px] text-slate-500">₹199/seat/mo</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setAddons(prev => ({ ...prev, extra_tracking_seats: Math.max(0, prev.extra_tracking_seats - 1) }))}
                          className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-white"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="text-[18px] font-bold text-slate-800 w-8 text-center">{addons.extra_tracking_seats}</span>
                        <button
                          onClick={() => setAddons(prev => ({ ...prev, extra_tracking_seats: prev.extra_tracking_seats + 1 }))}
                          className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-white"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Limit Overrides */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                  <h3 className="text-[14px] font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-rose-500" />
                    Limit Overrides (Phase 1 Must-Have)
                  </h3>
                  <p className="text-[12px] text-slate-500 mb-4">Leave blank to use plan defaults. These override the plan&apos;s limits for this business only.</p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { key: 'max_staff_total', label: 'Max Staff Total', icon: Users },
                      { key: 'max_tracking_staff', label: 'Max Tracking Staff', icon: MapPin },
                      { key: 'max_products_total', label: 'Max Products', icon: Package },
                      { key: 'max_customers_total', label: 'Max Customers', icon: Users },
                      { key: 'max_sales_invoices_per_cycle', label: 'Max Sales Invoices/Month', icon: CreditCard },
                      { key: 'max_purchase_invoices_per_cycle', label: 'Max Purchase Invoices/Month', icon: CreditCard },
                      { key: 'max_templates_total', label: 'Max Templates', icon: Package },
                      { key: 'max_exports_per_cycle', label: 'Max Exports/Month', icon: Package }
                    ].map(limit => (
                      <div key={limit.key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <limit.icon className="w-4 h-4 text-slate-400" />
                          <span className="text-[12px] text-slate-600">{limit.label}</span>
                        </div>
                        <input
                          type="number"
                          value={limitOverrides[limit.key] ?? ''}
                          onChange={(e) => setLimitOverrides(prev => ({
                            ...prev,
                            [limit.key]: e.target.value ? parseInt(e.target.value) : undefined
                          }))}
                          placeholder={limitsSummary?.plan_limits?.[limit.key] ?? 'Plan default'}
                          className="w-24 px-2 py-1 border border-slate-200 rounded text-[12px] text-right focus:outline-none focus:ring-2 focus:ring-rose-200"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column - Summary */}
              <div className="space-y-4">
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                  <h3 className="text-[14px] font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <Eye className="w-4 h-4 text-rose-500" />
                    Current Status
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between py-2 border-b border-slate-100">
                      <span className="text-[12px] text-slate-500">Industry</span>
                      <span className="text-[12px] font-medium text-slate-700">
                        {subscription?.industry_name || 'Not Set'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-slate-100">
                      <span className="text-[12px] text-slate-500">Plan</span>
                      <span className="text-[12px] font-medium text-teal-600">
                        {subscription?.plan_name || 'Not Set'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-slate-100">
                      <span className="text-[12px] text-slate-500">Status</span>
                      <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-emerald-100 text-emerald-700">
                        {subscription?.status || 'Active'}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <p className="text-[11px] font-medium text-slate-600 uppercase tracking-wider mb-2">Effective Limits</p>
                    <div className="space-y-2 text-[12px]">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Max Staff:</span>
                        <span className="font-medium text-slate-700">
                          {limitOverrides.max_staff_total ?? (selectedPlan?.included_staff || 0) + addons.extra_staff_seats}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Max Tracking:</span>
                        <span className="font-medium text-slate-700">
                          {limitOverrides.max_tracking_staff ?? (selectedPlan?.included_tracking_staff || 0) + addons.extra_tracking_seats}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Danger Zone: Suspend / Activate */}
                <div className="bg-white rounded-xl border border-red-200 shadow-sm p-5">
                  <h3 className="text-[14px] font-semibold text-red-700 mb-1 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Danger Zone
                  </h3>
                  <p className="text-[11px] text-slate-500 mb-4">
                    Suspending a business blocks all owner and staff access immediately.
                  </p>
                  {business?.status !== 'active' ? (
                    <button
                      onClick={() => setConfirmModal({
                        title: 'Activate Business',
                        description: 'This will restore full access for the business owner and all staff immediately.',
                        confirmLabel: 'Activate',
                        confirmClass: 'bg-emerald-600 hover:bg-emerald-700',
                        onConfirm: async () => {
                          try {
                            await api.patch(`/admin/businesses/${businessId}/status`, null, { params: { status: 'active' } });
                            toast.success('Business activated');
                            await fetchBusiness();
                          } catch (err) {
                            toast.error(getApiErrorMessage(err, 'Failed to activate'));
                          }
                        }
                      })}
                      className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg text-[13px] font-medium hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Activate Business
                    </button>
                  ) : (
                    <button
                      onClick={() => setConfirmModal({
                        title: 'Suspend Business',
                        description: 'This will immediately block all access for the business owner and staff. The business will be unable to log in until reactivated.',
                        confirmLabel: 'Suspend',
                        confirmClass: 'bg-red-600 hover:bg-red-700',
                        onConfirm: async () => {
                          try {
                            await api.patch(`/admin/businesses/${businessId}/status`, null, { params: { status: 'suspended' } });
                            toast.success('Business suspended');
                            await fetchBusiness();
                          } catch (err) {
                            toast.error(getApiErrorMessage(err, 'Failed to suspend'));
                          }
                        }
                      })}
                      className="w-full px-4 py-2 bg-red-600 text-white rounded-lg text-[13px] font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <XCircle className="w-4 h-4" />
                      Suspend Business
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Menu Control Tab */}
          {activeTab === 'menu' && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[13px] font-medium text-amber-800">Full Menu Control</p>
                    <p className="text-[11px] text-amber-700 mt-1">
                      Override what this business owner can see in their sidebar. 
                      <strong> Force ON</strong> = always show, <strong>Force OFF</strong> = always hide, <strong>Use Plan</strong> = follow plan settings.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
                  <h3 className="text-[15px] font-semibold text-slate-800">
                    Menu Modules for {business?.name}
                  </h3>
                  <p className="text-[12px] text-slate-500 mt-0.5">
                    Control exactly what appears in the Owner sidebar
                  </p>
                </div>
                
                <div className="divide-y divide-slate-100">
                  {menuConfig?.modules?.map(module => {
                    const ModuleIcon = MODULE_ICONS[module.key] || Package;
                    const colors = MODULE_COLORS[module.key] || MODULE_COLORS.home;
                    const isExpanded = expandedModules[module.key];
                    const moduleStatus = getOverrideStatus(module.key, moduleOverrides);
                    const hasChildren = module.children && module.children.length > 0;
                    
                    return (
                      <div key={module.key} data-testid={`menu-module-${module.key}`}>
                        {/* Module Header */}
                        <div className={`flex items-center justify-between px-5 py-4 ${colors.bg}`}>
                          <div className="flex items-center gap-3 flex-1">
                            <button
                              onClick={() => hasChildren && toggleModuleExpand(module.key)}
                              className="flex items-center gap-3"
                            >
                              {hasChildren && (
                                isExpanded ? 
                                  <ChevronDown className="w-4 h-4 text-slate-500" /> : 
                                  <ChevronRight className="w-4 h-4 text-slate-500" />
                              )}
                              <ModuleIcon className={`w-5 h-5 ${colors.text}`} />
                              <span className="text-[14px] font-semibold text-slate-800">{module.label}</span>
                            </button>
                            {hasChildren && (
                              <span className="text-[11px] text-slate-500 ml-2">
                                {module.children.length} submodules
                              </span>
                            )}
                          </div>
                          
                          {/* Module Override Toggle */}
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-1 rounded text-[10px] font-medium ${moduleStatus.bg} ${moduleStatus.color}`}>
                              {moduleStatus.text}
                            </span>
                            <button
                              onClick={() => toggleModuleOverride(module.key)}
                              className="transition-colors"
                              title="Click to cycle: Use Plan → Force ON → Force OFF"
                            >
                              {moduleOverrides[module.key] === true ? (
                                <ToggleRight className="w-10 h-10 text-emerald-500" />
                              ) : moduleOverrides[module.key] === false ? (
                                <ToggleLeft className="w-10 h-10 text-red-400" />
                              ) : (
                                <ToggleRight className="w-10 h-10 text-slate-300" />
                              )}
                            </button>
                          </div>
                        </div>
                        
                        {/* Submodules */}
                        {hasChildren && isExpanded && (
                          <div className="bg-white">
                            {module.children.map(child => {
                              const childStatus = getOverrideStatus(child.key, submoduleOverrides);
                              
                              return (
                                <div
                                  key={child.key}
                                  className="flex items-center justify-between px-5 py-3 pl-14 border-t border-slate-50 hover:bg-slate-50"
                                  data-testid={`menu-submodule-${child.key}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="w-2 h-2 rounded-full bg-slate-300" />
                                    <span className="text-[13px] text-slate-700">{child.label}</span>
                                    <span className="text-[10px] text-slate-400 font-mono">{child.route}</span>
                                  </div>
                                  
                                  <div className="flex items-center gap-3">
                                    <span className={`px-2 py-0.5 rounded text-[9px] font-medium ${childStatus.bg} ${childStatus.color}`}>
                                      {childStatus.text}
                                    </span>
                                    <button
                                      onClick={() => toggleSubmoduleOverride(child.key)}
                                      className="transition-colors"
                                    >
                                      {submoduleOverrides[child.key] === true ? (
                                        <ToggleRight className="w-8 h-8 text-emerald-500" />
                                      ) : submoduleOverrides[child.key] === false ? (
                                        <ToggleLeft className="w-8 h-8 text-red-400" />
                                      ) : (
                                        <ToggleRight className="w-8 h-8 text-slate-300" />
                                      )}
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Usage & Counters Tab */}
          {activeTab === 'usage' && (
            <div className="space-y-6">
              {/* Billing Cycle Info */}
              {limitsSummary?.billing_cycle && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                  <h3 className="text-[14px] font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-rose-500" />
                    Current Billing Cycle
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <p className="text-[11px] text-slate-500 uppercase tracking-wider">Cycle Start</p>
                      <p className="text-[14px] font-semibold text-slate-800 mt-1">
                        {new Date(limitsSummary.billing_cycle.start).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <p className="text-[11px] text-slate-500 uppercase tracking-wider">Cycle End</p>
                      <p className="text-[14px] font-semibold text-slate-800 mt-1">
                        {new Date(limitsSummary.billing_cycle.end).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="p-4 bg-rose-50 rounded-lg text-center">
                      <p className="text-[11px] text-rose-600 uppercase tracking-wider">Period Key</p>
                      <p className="text-[12px] font-mono text-rose-700 mt-1">
                        {limitsSummary.billing_cycle.period_key}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Static Limits */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <h3 className="text-[14px] font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-rose-500" />
                  Static Limits (Total Counts)
                </h3>
                <p className="text-[12px] text-slate-500 mb-4">These limits don&apos;t reset monthly. Block new creations only when exceeded.</p>
                
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                  {limitsSummary?.static_limits && Object.entries(limitsSummary.static_limits).map(([key, data]) => (
                    <div key={key} className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[12px] font-medium text-slate-600 capitalize">{key.replace(/_/g, ' ')}</span>
                        {data.max && data.current >= data.max && (
                          <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-medium">LIMIT REACHED</span>
                        )}
                      </div>
                      <div className="flex items-end justify-between">
                        <div>
                          <span className="text-[24px] font-bold text-slate-800">{data.current}</span>
                          <span className="text-[14px] text-slate-400">/{data.max ?? '∞'}</span>
                        </div>
                        <div className="text-right">
                          <p className="text-[11px] text-slate-500">Remaining</p>
                          <p className="text-[14px] font-semibold text-emerald-600">{data.remaining}</p>
                        </div>
                      </div>
                      {data.max && (
                        <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${
                              (data.current / data.max) >= 0.9 ? 'bg-red-500' : 
                              (data.current / data.max) >= 0.7 ? 'bg-amber-500' : 'bg-emerald-500'
                            }`}
                            style={{ width: `${Math.min(100, (data.current / data.max) * 100)}%` }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Monthly Counters */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-[14px] font-semibold text-slate-800 flex items-center gap-2">
                      <Eye className="w-4 h-4 text-rose-500" />
                      Monthly Usage Counters
                    </h3>
                    <p className="text-[12px] text-slate-500 mt-1">Resets at the start of each billing cycle</p>
                  </div>
                  <button
                    onClick={() => setConfirmModal({
                      title: 'Reset Usage Counters',
                      description: 'This will reset all monthly usage counters to zero. For support cases only.',
                      confirmLabel: 'Reset Counters',
                      confirmClass: 'bg-amber-600 hover:bg-amber-700',
                      onConfirm: async () => {
                        try {
                          await api.post(`/admin/businesses/${businessId}/usage/reset`);
                          toast.success('Usage counters reset successfully');
                          await fetchLimitsSummary();
                        } catch (e) {
                          toast.error(getApiErrorMessage(e, 'Error resetting counters'));
                        }
                      }
                    })}
                    className="px-3 py-1.5 text-[11px] font-medium text-amber-700 bg-amber-100 rounded-lg hover:bg-amber-200 transition-colors"
                  >
                    Reset Counters (Support)
                  </button>
                </div>
                
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {limitsSummary?.monthly_counters && Object.entries(limitsSummary.monthly_counters).map(([key, data]) => (
                    <div key={key} className="p-4 bg-gradient-to-br from-slate-50 to-white rounded-lg border border-slate-100">
                      <p className="text-[11px] text-slate-500 font-medium uppercase tracking-wider mb-2">
                        {key.replace(/_/g, ' ')}
                      </p>
                      <div className="flex items-baseline gap-1">
                        <span className="text-[20px] font-bold text-slate-800">{data.current || 0}</span>
                        <span className="text-[13px] text-slate-400">/ {data.max ?? '∞'}</span>
                      </div>
                      {data.max && (
                        <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              ((data.current || 0) / data.max) >= 0.9 ? 'bg-red-500' : 
                              ((data.current || 0) / data.max) >= 0.7 ? 'bg-amber-500' : 'bg-rose-500'
                            }`}
                            style={{ width: `${Math.min(100, ((data.current || 0) / data.max) * 100)}%` }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Plan Limits Reference */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <h3 className="text-[14px] font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Package className="w-4 h-4 text-rose-500" />
                  All Plan Limits (Reference)
                </h3>
                <p className="text-[12px] text-slate-500 mb-4">These are the effective limits after plan + add-ons + overrides.</p>
                
                <div className="grid grid-cols-3 lg:grid-cols-5 gap-3">
                  {limitsSummary?.plan_limits && Object.entries(limitsSummary.plan_limits).map(([key, value]) => (
                    <div key={key} className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider truncate" title={key}>
                        {key.replace(/max_|_per_cycle|_total/g, '').replace(/_/g, ' ')}
                      </p>
                      <p className="text-[16px] font-bold text-slate-800 mt-1">{value ?? '∞'}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        onConfirm={async () => {
          const action = confirmModal?.onConfirm;
          setConfirmModal(null);
          await action?.();
        }}
        onClose={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default BusinessDetails;
